import React, { useRef } from 'react';
import { motion, useInView, Variants } from 'framer-motion';

const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
};

const itemVariants: Variants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: { 
        opacity: 1, 
        y: 0, 
        scale: 1,
        transition: { type: "spring", stiffness: 100, damping: 20 }
    }
};

export const TechGrid: React.FC = () => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section className="py-16 md:py-32 bg-black text-white px-4" ref={ref}>
      <div className="container mx-auto max-w-7xl">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h2 className="font-heading text-3xl md:text-5xl lg:text-7xl text-white mb-2 tracking-tight">
              Innovation That
            </h2>
            <h2 className="font-heading text-3xl md:text-5xl lg:text-7xl text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-500">
              Drives Precision
            </h2>
          </motion.div>
          
          <motion.div
            className="flex items-center gap-4 mt-8 md:mt-0 bg-white/5 px-6 py-3 rounded-full border border-white/10 backdrop-blur-sm"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.1)" }}
          >
            <div className="text-right">
              <div className="flex text-[#FF4D9E] text-lg">★★★★☆</div>
              <p className="text-sm text-gray-400 mt-1 font-medium">Trusted By Many People</p>
            </div>
           <img src="/Images/tech/tech_c.webp" alt="Technology Innovation" className="w-16 h-10 object-cover rounded-lg opacity-80" />
          </motion.div>
        </div>

        {/* Bento Grid */}
        <motion.div 
            className="grid grid-cols-1 md:grid-cols-4 gap-6 h-auto md:h-[900px]"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
        >
          
          {/* Column 1 */}
          <div className="col-span-1 flex flex-col gap-6 h-full">
            {/* Quote Card - tech_tl */}
            <motion.div 
               className="relative overflow-hidden rounded-[30px] h-1/2 group cursor-pointer"
               variants={itemVariants}
               whileHover={{ scale: 0.98 }}
            >
                <motion.img 
                    src="/Images/tech/tech_tl.webp" 
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.15 }}
                    transition={{ duration: 0.6 }}
                />
                <div className="absolute inset-0 p-6 flex items-center justify-center text-center">
                    <p className="font-serif italic text-lg leading-relaxed text-white backdrop-blur-md bg-white/10 p-4 rounded-xl border border-white/20">
                    "At Printaura, our packaging output is driven by cutting-edge machinery."
                    </p>
                </div>
                {/* Always visible gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80" />
                <div className="absolute bottom-6 left-6 right-6">
                    <div className="h-1 w-10 bg-[#FF4D9E] mb-2" />
                    <p className="font-heading text-xl">Precision Logic</p>
                </div>
            </motion.div>
            
            {/* Product Card - tech_lb */}
            <motion.div 
               className="relative overflow-hidden rounded-[30px] h-1/2 group"
               variants={itemVariants}
            >
                <motion.img 
                    src="/Images/tech/tech_lb.webp" 
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.1, rotate: 2 }}
                    transition={{ duration: 0.7 }}
                />
                 <div className="absolute inset-0 bg-gradient-to-tr from-[#3D0C23]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
            </motion.div>
          </div>

          {/* Column 2 - Main Center Image (tech_c) - Sustainable Production */}
          <motion.div 
            className="col-span-1 md:col-span-2 relative overflow-hidden rounded-[30px] group h-[400px] md:h-full"
            variants={itemVariants}
          >
            <motion.img 
                src="/Images/tech/tech_c.webp" 
                className="w-full h-full object-cover"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.8 }}
            />
            {/* Dark gradient at bottom */}
            <div className="absolute bottom-0 left-0 w-full h-2/3 bg-gradient-to-t from-black/95 via-black/50 to-transparent" />
            
            <div className="absolute bottom-0 left-0 w-full p-8 md:p-12">
                <motion.div 
                    initial={{ y: 20, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="backdrop-blur-md bg-white/5 p-6 rounded-2xl border border-white/10"
                >
                    <span className="inline-block px-3 py-1 rounded-full border border-green-500/50 text-green-400 text-xs mb-4 bg-green-900/20 backdrop-blur-md">
                        ECO-CONSCIOUS
                    </span>
                    <h3 className="text-3xl md:text-5xl font-heading mb-4 text-white">Sustainable Production Systems</h3>
                    <p className="text-gray-300 text-base md:text-lg mb-6 max-w-lg">
                        Eco-Friendly & Responsible Manufacturing driven by renewable energy and recyclable materials.
                    </p>
                    <div className="flex flex-wrap gap-4">
                        <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-md border border-white/5">
                            <span className="text-green-400">♻</span> <span className="text-xs text-white">Recyclable Boards</span>
                        </div>
                        <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-md border border-white/5">
                            <span className="text-green-400">⚡</span> <span className="text-xs text-white">Low Energy</span>
                        </div>
                        <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-md border border-white/5">
                            <span className="text-green-400">💧</span> <span className="text-xs text-white">Water-based Inks</span>
                        </div>
                    </div>
                </motion.div>
            </div>
          </motion.div>

          {/* Column 3 */}
          <div className="col-span-1 flex flex-col gap-6 h-full">
             {/* Stats Cards */}
             <div className="flex-1 flex flex-col gap-4">
                 {[
                    { val: '98.6%', label: 'Production Accuracy' },
                    { val: '96%', label: 'Machine Uptime Efficiency' },
                    { val: '78%', label: 'Sustainable Material Usage' }
                 ].map((stat, i) => (
                    <motion.div 
                        key={i} 
                        className="bg-[#0A0A0A] rounded-[24px] p-6 border border-white/5 relative overflow-hidden flex-1 flex flex-col justify-center group"
                        variants={itemVariants}
                        whileHover={{ y: -5, borderColor: "rgba(255, 77, 158, 0.3)" }}
                    >
                         <div className="absolute top-0 right-0 w-20 h-20 bg-[#FF4D9E] blur-[50px] opacity-0 group-hover:opacity-20 transition-opacity duration-500" />
                        <h4 
                          className="text-4xl font-bold mb-2 text-transparent bg-clip-text"
                          style={{ backgroundImage: 'linear-gradient(264.39deg, #FFFFFF 35.69%, #F60081 112.49%)' }}
                        >
                          {stat.val}
                        </h4>
                        <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">{stat.label}</p>
                    </motion.div>
                 ))}
             </div>

             {/* Box Kit Image - tech_rb */}
             <motion.div 
                className="h-1/3 relative overflow-hidden rounded-[30px] group"
                variants={itemVariants}
             >
                <motion.img 
                    src="/Images/tech/tech_rb.webp" 
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.15, rotate: -2 }}
                    transition={{ duration: 0.7 }}
                />
                 <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors" />
             </motion.div>
          </div>

        </motion.div>
      </div>
    </section>
  );
};
